/**
 * @file LenisProvider.tsx
 * @description Smooth scroll provider using Lenis for butter-smooth scrolling.
 *
 * Lenis is loaded LAZILY after first paint. On FCP we render `children`
 * directly and only pull in `lenis/react` (~30KB transitive) on idle.
 * This keeps the smooth-scroll library off the critical path while still
 * upgrading the UX for desktop pointer users.
 *
 * Respects reduced motion, touch devices, and a QA bypass.
 */

import { type ReactNode, useEffect, useState, type ComponentType } from "react";

import { useReducedMotion } from "@/lib/motion";

interface LenisProviderProps {
  children: ReactNode;
}

type LenisComponent = ComponentType<{ root?: boolean; options?: Record<string, unknown>; children?: ReactNode }>;

/**
 * QA bypass: when the URL contains `?qa=1` or `?lenis=0`, Lenis is disabled
 * so automated browser tools (which dispatch synthetic scroll events) can
 * drive the page reliably. Real users are unaffected.
 */
function isQaBypass(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const sp = new URLSearchParams(window.location.search);
    return sp.get("qa") === "1" || sp.get("lenis") === "0";
  } catch {
    return false;
  }
}

export function LenisProvider({ children }: LenisProviderProps) {
  const prefersReducedMotion = useReducedMotion();
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [qaBypass] = useState(() => isQaBypass());
  const [ReactLenis, setReactLenis] = useState<LenisComponent | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mediaQuery = window.matchMedia("(pointer: coarse)");
    const updateTouchStatus = () => {
      const hasTouchPoints = navigator.maxTouchPoints > 0;
      setIsTouchDevice(mediaQuery.matches || hasTouchPoints);
    };

    updateTouchStatus();
    mediaQuery.addEventListener?.("change", updateTouchStatus);
    mediaQuery.addListener?.(updateTouchStatus);

    return () => {
      mediaQuery.removeEventListener?.("change", updateTouchStatus);
      mediaQuery.removeListener?.(updateTouchStatus);
    };
  }, []);

  // Disable smooth scroll for reduced motion, touch devices, or QA bypass —
  // never load the library in these cases.
  const shouldDisable = prefersReducedMotion || isTouchDevice || qaBypass;

  // Lazy-load lenis/react after first paint to keep it off the FCP critical path.
  useEffect(() => {
    if (shouldDisable || ReactLenis) return;

    let cancelled = false;
    const load = () => {
      import("lenis/react")
        .then((mod) => {
          if (!cancelled) setReactLenis(() => mod.ReactLenis as LenisComponent);
        })
        .catch(() => {
          /* Smooth scroll is a progressive enhancement — silently fall back. */
        });
    };

    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    };
    if (typeof w.requestIdleCallback === "function") {
      w.requestIdleCallback(load, { timeout: 2500 });
    } else {
      setTimeout(load, 800);
    }

    return () => {
      cancelled = true;
    };
  }, [shouldDisable, ReactLenis]);

  if (shouldDisable || !ReactLenis) {
    return <>{children}</>;
  }

  return (
    <ReactLenis
      root
      options={{
        lerp: 0.1,
        duration: 1.2,
        smoothWheel: true,
        wheelMultiplier: 1,
        touchMultiplier: 2,
        syncTouch: true,
        syncTouchLerp: 0.075,
      }}
    >
      {children}
    </ReactLenis>
  );
}
